import React, { useEffect, useState } from 'react';
import { FieldErrors } from 'react-hook-form';
import { ToastContainer } from 'react-toastify';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { useFinalSubmissionStore } from 'src/store/final-submission';
import { useUnderReviewStore } from 'src/store/under-review';
import { useTourStore } from 'src/store/tour';
import { useAuthStore } from 'src/store/auth';
import { useApplicationStore } from 'src/store/application';
import { useAcceptanceChangeStore } from 'src/store/acceptance-change';
import {
  UnderReview,
  AfterSignIn,
  TourGuide,
  AcceptanceWarningModal,
} from '@features/onboarding/components/shared/modal';
import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import { applicationService } from 'src/services/application';

export const OnboardingForm = () => {
  const {
    selectedStep,
    currentFormStep,
    formSteps,
    handleNextStep,
    setData,
    setTotalSteps,
  } = useFormControls();

  const { underwritingResult } = useFinalSubmissionStore();
  const {
    isModalOpen: isUnderReviewModalOpen,
    setModalOpen: setResetModalOpen,
  } = useUnderReviewStore();
  const { updateTierFromApplicationState, applications } =
    useApplicationStore();

  const { isTourGuideOpen, openTourGuide, checkShouldShowTour } =
    useTourStore();
  const { isAuthenticated } = useAuthStore();
  const { signedAt } = useApplicationStore();
  const { isModalOpen: isAcceptanceWarningModalOpen, checkApplicationStatus } =
    useAcceptanceChangeStore();
  const StepComponent = currentFormStep?.Component;
  const isLastStep = selectedStep === formSteps?.length - 1;

  // Flag to prevent navigation logic from triggering on every step change
  // Use sessionStorage to persist across re-renders/re-mounts
  const SESSION_KEY = 'onboarding_navigation_checked';
  const hasCheckedInitialState = () => {
    return sessionStorage.getItem(SESSION_KEY) === 'true';
  };
  const setHasCheckedInitialState = (value: boolean) => {
    if (value) {
      sessionStorage.setItem(SESSION_KEY, 'true');
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  };

  // remember data when user change step
  const handleChange = React.useCallback(
    (data: Partial<OnboardingSchema>) => {
      setData(data);
    },
    [setData]
  );

  const handleSubmitForm = async (data: Partial<OnboardingSchema>) => {
    setData(data);
  };

  function getFirstErrorMessage(obj: FieldErrors): string | null {
    if (typeof obj !== 'object' || obj === null) {
      return null;
    }

    if (obj?.message) {
      return obj.message as unknown as string;
    }

    const keys = Object.keys(obj);
    if (keys.length === 0) {
      return null;
    }

    const firstKey = keys[0];
    const firstValue = obj[firstKey];

    if (typeof firstValue === 'object' && firstValue !== null) {
      const message = getFirstErrorMessage(firstValue as FieldErrors);
      if (message) {
        return message;
      }
    }

    return null;
  }
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);
  // const handleInvalid = (errors: FieldErrors) => {
  //   const errorMessage = getFirstErrorMessage(errors);
  //   if (errorMessage) {
  //     toast.error(errorMessage);
  //   }
  // };
  const handleInvalid = (errors: FieldErrors) => {
    const errorMessage = getFirstErrorMessage(errors);
    setFormErrorMessage(errorMessage || null);
  };
  useEffect(() => {
    setTotalSteps(formSteps.length);
  }, [formSteps.length, setTotalSteps]);

  // const { resetState } = useFinalSubmissionStore();

  useEffect(() => {
    console.log('applications', applications);
    updateTierFromApplicationState();
  }, []);

  // useEffect(() => {
  //   if (!isLastStep && underwritingResult?.status === 'rejected') {
  //     resetState();
  //   }
  // }, [isLastStep]);

  // Check when underwriting result changes to show under review modal
  useEffect(() => {
    if (underwritingResult?.status === 'human_review') {
      setResetModalOpen(true);
    }
  }, [underwritingResult, setResetModalOpen]);

  // Reset the flag if user is not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      console.log(
        '[OnboardingForm] User not authenticated, clearing navigation check flag'
      );
      setHasCheckedInitialState(false);
    }
  }, [isAuthenticated]);

  // Check application state for phqUnderReviewAt and phqAcceptedAt
  // Also handle navigation on page load to prevent form getting stuck
  useEffect(() => {
    const checkApplicationState = async () => {
      // Only check once on initial load to prevent unwanted navigation on every step change
      console.log(
        '[OnboardingForm] checkApplicationState - hasChecked:',
        hasCheckedInitialState(),
        'selectedStep:',
        selectedStep,
        'currentStepId:',
        formSteps[selectedStep]?.id
      );
      if (hasCheckedInitialState()) {
        console.log(
          '[OnboardingForm] Already checked, skipping navigation logic'
        );
        return;
      }

      try {
        const applicationState = await applicationService.applicationState();
        console.log('[OnboardingForm] applicationState:', {
          phqAcceptedAt: applicationState?.phqAcceptedAt,
          phqRejectedAt: applicationState?.phqRejectedAt,
          phqUnderReviewAt: applicationState?.phqUnderReviewAt,
          signedAt,
        });

        // Check if application is accepted (similar to under-review logic)
        if (
          applicationState?.phqAcceptedAt ||
          applicationState?.phqRejectedAt ||
          applicationState?.phqUnderReviewAt
        ) {
          console.log('selectedStep', selectedStep);
          checkApplicationStatus(selectedStep === 1);
        }

        // BUG FIX: Handle navigation on page load if user has already completed PHQ
        // Prevent form from getting stuck after PHQ on reload
        if (applicationState?.phqAcceptedAt && !signedAt) {
          // User has accepted tier but hasn't signed - ensure they're on Health Plans or later
          const currentStep = formSteps[selectedStep];
          if (
            currentStep?.id === 'phq' ||
            currentStep?.sectionName === 'Health Questions'
          ) {
            // User is stuck on PHQ step - move them to Health Plans
            console.log(
              '[OnboardingForm] User accepted, navigating to Medical from PHQ'
            );
            handleNextStep({});
            setHasCheckedInitialState(true);
            setTimeout(() => {}, 500);
            return;
          }
        } else if (applicationState?.phqRejectedAt && !signedAt) {
          // User is rejected - only auto-navigate if stuck on PHQ, NOT from health_plans
          const currentStep = formSteps[selectedStep];
          console.log(
            '[OnboardingForm] User rejected, currentStep:',
            currentStep?.id
          );
          if (currentStep?.id === 'phq') {
            // Only skip from PHQ step, not from health_plans
            console.log(
              '[OnboardingForm] Skipping to Dental from PHQ - user rejected'
            );
            handleNextStep({});
            setHasCheckedInitialState(true);
            setTimeout(() => {}, 500);
            return;
          }
        }

        // Mark as checked even if no navigation occurred
        console.log(
          '[OnboardingForm] Marking as checked, no navigation needed'
        );
        setHasCheckedInitialState(true);
      } catch (error) {
        console.error('Failed to check application state:', error);
        setHasCheckedInitialState(true);
      }
    };

    if (isAuthenticated) {
      checkApplicationState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, signedAt]);

  // Show tour guide after authentication and if conditions are met
  useEffect(() => {
    if (isAuthenticated && checkShouldShowTour()) {
      // Small delay to ensure the page is fully loaded
      const timer = setTimeout(() => {
        openTourGuide();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, checkShouldShowTour, openTourGuide]);

  return (
    <div
      id="step-parent"
      className="h-full relative overflow-y-clip max-md:mb-[100px] max-2xl:mb-[82px]"
    >
      {StepComponent && (
        <StepComponent
          title={currentFormStep?.title}
          sectionName={currentFormStep?.sectionName}
          description={currentFormStep?.description}
          subscription={currentFormStep?.subscription}
          onSubmit={isLastStep ? handleSubmitForm : handleNextStep}
          onChange={handleChange}
          onInvalid={handleInvalid}
          externalErrorMessage={formErrorMessage}
          defaultStep={currentFormStep?.defaultStep}
        />
      )}
      <ToastContainer />
      {isUnderReviewModalOpen && <UnderReview />}
      {isTourGuideOpen && <TourGuide />}
      {isAcceptanceWarningModalOpen &&
        (signedAt === null ? <AcceptanceWarningModal /> : <AfterSignIn />)}
    </div>
  );
};
